import CircularProgress from '@material-ui/core/CircularProgress';
import * as React from 'react';
import {NotificationManager} from 'react-notifications';
import * as _ from 'lodash';
import {DELETE, GET_ONE, UPDATE} from '../fetchApi/types';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {compose, Dispatch} from 'redux';
import {connect} from 'react-redux';
import {reset} from 'redux-form';
import {
    dataIsValidAffectedIds,
    dataIsValidAfterSave,
    dataValidityKey,
    realtimeDataReducerKey,
} from '../constants';
import {changeAfterSaveDataValidity} from '../actions/RealtimeActions';
import {withTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {withDataProvider} from '../fetchApi/dataProvider';
import {DataProvider} from '../fetchApi/tsTypes';

const circularProgressStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
};

interface EnhancedEditProps {
    id?: number | string;
    resource: string;
    include?: Array<object | string>;
}

interface EditProps extends RouteComponentProps, EnhancedEditProps {
    dispatch: Dispatch;
    updatedIds: Array<any>;
    t: TFunction;
    dataProvider: DataProvider;
    formName: string;
    reset: Function;
}

interface EditState {
    record: Record<string, any>;
    id: number | string | null;
    loading: boolean;
    serverVersion: string | null;
}

export interface InjectingEditProps {
    save: Function;
    handleDelete: (id?: number | string) => void;
    id: number | string;
    record: {
        [key in string]: any
    };
    loading: boolean;
    resource: string;
    serverVersion: string;
}

class Edit extends React.Component<EditProps, EditState> {
    constructor(props: EditProps) {
        super(props);
        this.state = {
            record: {},
            id: null,
            loading: false,
            serverVersion: null
        };
    }

    save = (values: Record<string, any>, redirect?: string): void => {
        const {resource, t: translate, dataProvider, history} = this.props;
        const {id} = this.state;

        // this.setState({loading: true});

        dataProvider(UPDATE, resource, {data: values, id})
            .then(() => {
                NotificationManager.success(
                    translate('common.notifications.element_updated'),
                    '',
                    1500,
                );

                if (redirect) {
                    history.push(redirect);
                }
            })
            .catch((e) => {
                console.error(e);
                NotificationManager.error(
                    e.message,
                    translate('common.notifications.error_title'),
                    2500,
                );
            })
            .finally(() => {
                // this.setState({loading: false});
            });
    };

    delete = (): void => {
        const {history, resource, t: translate, dataProvider} = this.props;
        const {id} = this.state;
        dataProvider(DELETE, resource, {id})
            .then(() => {
                NotificationManager.success(
                    translate('common.notifications.element_deleted'),
                    '',
                    2500,
                );
                history.push(`/${resource}`);
            })
            .catch((e) => {
                console.error(e);
                NotificationManager.error(
                    e.message,
                    translate('common.notifications.error_title'),
                    2500,
                );
            });
    };

    getId = (match: RouteComponentProps['match']): number | string | null => {
        const id = _.get(match, 'params.id', null);
        if (id) {
            return id;
        } else {
            console.error('Cannot parse id from url, be sure that you use component in right place');
            return null;
        }
    };

    loadData = (): void => {
        const {id: propsId, resource, match, dispatch, dataProvider, history, include, t: translate, formName, reset} = this.props;

        const id = propsId || this.getId(match);
        if (id) {
            this.setState({loading: true});

            dataProvider(GET_ONE, resource, {id, include})
                .then(data => {
                    this.setState({record: data.data, id: id, loading: false, serverVersion: data.serverVersion});
                    reset(formName); // TODO fix highlighter after save
                })
                .catch((e) => {
                    const translationKey= `${resource}.single`;
                    const translated = translate(translationKey);
                    const resourceName = translated === translationKey ? resource : translated;

                    NotificationManager.error(translate('common.resource_not_found_error', {resourceName, id}));
                    this.setState({loading: false});
                    console.error(e);
                    history.push(`/${resource}`);
                });

            // Flush after save data validity flag
            const validateDataAction = changeAfterSaveDataValidity({resource, payload: []});
            dispatch(validateDataAction);
        } else {
            throw new Error('Cannot define id for record, please, be sure than you made everything as needed');
        }
    };

    componentDidUpdate(prevProps: Readonly<EditProps>): void {
        const {match, id: propsId, updatedIds} = this.props;
        const {id: prevPropsId, match: prevPropsMatch} = prevProps;
        const {id: stateId, loading} = this.state;

        const prevId = prevPropsId || this.getId(prevPropsMatch);
        const currentId = propsId || this.getId(match);

        if (
            !loading && (
                (prevId && currentId && !_.isEqual(prevId.toString(), currentId.toString())) ||
                (updatedIds?.find(id => id.toString() === stateId?.toString()))
            )
        ) {
            this.loadData();
        }
    }

    componentDidMount(): void {
        this.loadData();
    }

    render(): React.ReactElement {
        const {children, staticContext, id: propsId, resource, ...rest} = this.props;
        const {id, record, loading, serverVersion} = this.state;

        if (!record || !Object.keys(record).length) {
            return (
                <div style={circularProgressStyles}>
                    <CircularProgress size={75} />
                </div>
            );
        }

        return (
            <React.Fragment>
                {React.Children.map(children, (child: any) => {
                    return React.cloneElement(child, {
                        save: this.save,
                        handleDelete: this.delete,
                        id,
                        record,
                        loading,
                        serverVersion,
                        resource,
                        ...rest,
                    });
                })}
            </React.Fragment>

        );
    }
}

const mapStateToProps = (state: object, props: EditProps): object => {
    const dataIsValid = _.get(state, [realtimeDataReducerKey, props.resource, dataValidityKey, dataIsValidAfterSave], true);

    let updatedIds = [];
    if (!dataIsValid) {
        updatedIds = _.get(state, [realtimeDataReducerKey, props.resource, dataValidityKey, dataIsValidAffectedIds], []);
    }

    return ({updatedIds});
};

// @ts-ignore
const EnhancedEdit: React.FunctionComponent<EnhancedEditProps> = compose(
    withRouter,
    connect(mapStateToProps, {reset}),
    withTranslation(),
    withDataProvider,
)(Edit);

export default EnhancedEdit;
